/**
 * nexus-agents voter execution utilities
 *
 * Vote execution helpers including result creation, timeout handling,
 * retry logic, and simulation fallback.
 *
 * (Source: Extracted from voter-agents.ts per Issue #285)
 */

import type { Vote } from '../consensus/types.js';
import type { VoterRole, AgentVoteResult } from './vote-types.js';
import type { IModelAdapter, CompletionRequest, ILogger } from '../core/index.js';
import { getRandomProvider } from '../core/index.js';
import { delay, withTimeout } from '../utils/async-utils.js';
import { getVoterPrompts, SIMULATED_VOTE_REASONING } from './voter-prompts.js';
import {
  buildVotePrompt,
  parseVoteResponse,
  SyntheticVoteError,
  VOTE_JSON_SCHEMA,
} from './voter-response.js';

// Import timeout constants from canonical source (Issue #984)
import {
  VOTE_TIMEOUTS,
  resolveVoteTimeout as _resolveVoteTimeout,
  validateTimeout as _validateTimeout,
} from '../config/timeouts.js';
import { CLI_NAMES, type CliNameLiteral } from '../config/model-capabilities-types.js';
import { isAuthFailureText } from '../cli-adapters/cli-error-envelope.js';
import { sanitizeOutput } from '../security/output-sanitizer.js';
import { extractTextFromResponse } from './voter-response-text.js';

export { extractTextFromResponse };

/** Default vote timeout. Canonical source: `config/timeouts.ts`. */
export const DEFAULT_VOTE_TIMEOUT_MS = VOTE_TIMEOUTS.defaultMs;

/** Resolves vote timeout with env var override. Canonical: `config/timeouts.ts`. */
export const resolveVoteTimeout = _resolveVoteTimeout;

/** Maximum vote timeout. Canonical source: `config/timeouts.ts`. */
export const MAX_VOTE_TIMEOUT_MS = VOTE_TIMEOUTS.maxMs;

/** Minimum vote timeout. Canonical source: `config/timeouts.ts`. */
export const MIN_VOTE_TIMEOUT_MS = VOTE_TIMEOUTS.minMs;

/** Maximum retries per vote. Canonical source: `config/timeouts.ts`. */
export const DEFAULT_MAX_RETRIES = VOTE_TIMEOUTS.maxRetries;

/**
 * Initial retry delay in milliseconds.
 */
const INITIAL_RETRY_DELAY_MS = 1_000;

/**
 * Retry delay for rate-limit errors in milliseconds (Issue #1319).
 * Longer than standard to respect API rate limits.
 */
export const RATE_LIMIT_RETRY_DELAY_MS = 5_000;

/**
 * Detects whether an error message indicates a rate-limit condition.
 * Delegates to canonical rate-limit-detector (DRY consolidation Issue #1596).
 */
import { isRateLimitLikeError, isDurableCapacityText } from '../adapters/rate-limit-detector.js';

/** @see isRateLimitLikeError — re-exported for backward compatibility */
export function isRateLimitError(message: string): boolean {
  return isRateLimitLikeError(new Error(message));
}

/**
 * Validates and clamps timeout to `[VOTE_TIMEOUTS.minMs, VOTE_TIMEOUTS.maxMs]`.
 *
 * **Canonical source:** `config/timeouts.ts`. This re-export exists for
 * back-compat — new code should import from `../config/timeouts.js`
 * directly (#2637).
 */
export const validateTimeout = _validateTimeout;

// ============================================================================
// Vote Result Helpers
// ============================================================================

/**
 * Creates an error vote result (abstain with error message).
 * Issue #523: Uses source: 'error' instead of 'llm' for accuracy.
 */
export function createErrorVoteResult(
  role: VoterRole,
  errorMsg: string,
  processingTimeMs: number,
  providerId?: string
): AgentVoteResult {
  const bareCli =
    providerId?.startsWith('cli-') === true ? providerId.slice('cli-'.length) : providerId;
  const cli =
    bareCli !== undefined && (CLI_NAMES as readonly string[]).includes(bareCli)
      ? (bareCli as CliNameLiteral)
      : undefined;
  return {
    role,
    vote: {
      decision: 'abstain',
      reasoning: `[Error] Vote execution failed: ${errorMsg}`,
      confidence: 0,
    },
    processingTimeMs,
    source: 'error',
    error: errorMsg,
    ...(cli !== undefined ? { cli } : {}),
  };
}

/**
 * Creates a simulation vote result.
 */
export function createSimulationVoteResult(
  role: VoterRole,
  proposal: string,
  processingTimeMs: number,
  error?: string
): AgentVoteResult {
  return {
    role,
    vote: simulateVote(role, proposal),
    processingTimeMs,
    source: 'simulation',
    ...(error !== undefined && { error }),
  };
}

/**
 * Creates simulated votes for multiple roles.
 */
export function createSimulatedVotes(
  roles: readonly VoterRole[],
  proposal: string,
  error?: string
): readonly AgentVoteResult[] {
  const random = getRandomProvider();
  return roles.map((role) =>
    createSimulationVoteResult(role, proposal, random.randomInt(0, 100), error)
  );
}

/**
 * Role-specific vote distributions for simulation.
 * Each role has weighted probabilities reflecting their typical concerns:
 * - security: More skeptical, finds potential issues
 * - architect: Technically focused, generally supportive of good design
 * - devex: Balanced, considers usability
 * - ai_ml: Technically focused, evaluates AI aspects
 * - pm: Business focused, generally supportive of value
 *
 * Format: [approve_weight, reject_weight, abstain_weight]
 */
const ROLE_VOTE_DISTRIBUTIONS: Record<VoterRole, [number, number, number]> = {
  security: [40, 45, 15], // More skeptical - security concerns
  architect: [55, 30, 15], // Generally approving of good design
  devex: [50, 30, 20], // Balanced - considers usability
  ai_ml: [55, 30, 15], // Technical focus
  pm: [55, 25, 20], // Business focus - generally supportive
  catfish: [20, 65, 15], // Deliberately contrarian - challenges proposals (arXiv:2505.21503)
  scope_steward: [25, 60, 15], // Default-bias toward not shipping (#2185)
};

/**
 * Selects a decision based on weighted probabilities.
 */
function selectWeightedDecision(
  weights: [number, number, number]
): 'approve' | 'reject' | 'abstain' {
  const random = getRandomProvider();
  const total = weights[0] + weights[1] + weights[2];
  const rand = random.random() * total;

  if (rand < weights[0]) return 'approve';
  if (rand < weights[0] + weights[1]) return 'reject';
  return 'abstain';
}

/**
 * Fallback simulation when LLM is unavailable.
 * Uses role-specific vote distributions to provide more realistic simulation.
 * Clearly marks output as simulated.
 *
 * (Improved per Issue #453 - remove hardcoded 60% approve bias)
 */
export function simulateVote(role: VoterRole, proposal: string): Vote {
  const random = getRandomProvider();
  const weights = ROLE_VOTE_DISTRIBUTIONS[role];
  const decision = selectWeightedDecision(weights);

  // Confidence varies by decision type and role
  // Rejections tend to be higher confidence (found specific issue)
  // Approvals are moderate confidence (no issues found, but limited analysis)
  // Abstains are low confidence (insufficient information)
  let baseConfidence: number;
  if (decision === 'reject') {
    baseConfidence = 0.6 + random.random() * 0.3; // 0.6-0.9
  } else if (decision === 'approve') {
    baseConfidence = 0.5 + random.random() * 0.3; // 0.5-0.8
  } else {
    baseConfidence = 0.3 + random.random() * 0.2; // 0.3-0.5
  }

  return {
    decision,
    reasoning: `[Simulated - no LLM available] ${SIMULATED_VOTE_REASONING[role]} Proposal: "${proposal.slice(0, 50)}..."`,
    confidence: baseConfidence,
  };
}

// ============================================================================
// Timeout and Retry Utilities
// ============================================================================

// Re-export from canonical source for backward compatibility
export { withTimeout, delay } from '../utils/async-utils.js';

// ============================================================================
// Vote Attempt Execution
// ============================================================================

/**
 * Executes a single vote attempt (no retries).
 *
 * By default, throws SyntheticVoteError if response parsing fails.
 * This ensures we only get real LLM votes, not synthetic fallbacks.
 * (Source: Issue #512 - Fail-safe voting)
 */
/**
 * Builds the vote completion request. `withResponseFormat` toggles the native
 * structured-output ask (#3433): on for the first attempt, off for the #3497
 * retry against backends that route `json_schema` through provider tool-use.
 * The `parseVoteResponse` regex/Zod path below accepts prose-wrapped JSON, so
 * omitting `responseFormat` is safe — it just loses the schema-enforced shape.
 */
function buildVoteRequest({
  role,
  proposal,
  timeoutMs,
  withResponseFormat,
  options,
  project,
  workspace,
  workspaceSha,
}: VoteCompletionArgs): CompletionRequest {
  const base: CompletionRequest = {
    messages: [
      // #6110: the seat judges the caller's target project, not this
      // repository's. `undefined` renders the `nexus-agents` default.
      { role: 'system', content: getVoterPrompts(project)[role] },
      // #6254: and the working directory the seats run in, so a seat with
      // file tools knows where the artifact is and that reading it is expected.
      { role: 'user', content: buildVotePrompt(proposal, options, workspace, workspaceSha) },
    ],
    // 4000 (#4131): headroom so a findings-bearing verdict (JSON envelope +
    // reasoning + structured findings) isn't cut mid-JSON by the token cap and
    // silently dropped. Was 2000 (#2245, up from 500); large contrarian findings
    // still overflowed it. Non-findings votes stop at natural completion, so the
    // higher cap adds no cost for them.
    maxTokens: 4000,
    temperature: 0.3, // Low temperature for consistent evaluations
    // Thread the vote budget so the CLI timeout doesn't fire first (#3304); pass
    // signal too for CLI-vs-API cancellation parity (#3036/#3304).
    timeoutMs,
    ...(workspace !== undefined && workspace.trim() !== '' ? { workDir: workspace } : {}),
    signal: AbortSignal.timeout(timeoutMs),
  };
  return withResponseFormat
    ? { ...base, responseFormat: { type: 'json_schema', schema: VOTE_JSON_SCHEMA } }
    : base;
}

/**
 * #3497: some backends don't silently ignore an unsupported `responseFormat`.
 * OpenRouter implements `json_schema` via provider tool-use, so a role routed to
 * a provider without tool-use returns a hard 404 "No endpoints found that
 * support tool use" instead of ignoring the field — silently shrinking the panel
 * (observed on devex/catfish). Detect it so the caller retries without it.
 */
function isStructuredOutputUnsupported(errorMessage: string): boolean {
  return /support tool use/i.test(errorMessage);
}

/**
 * Per-call token usage reported by the adapter for one voter completion (#3910).
 * Propagated up so per-decision cost aggregation can attribute spend per voter.
 *
 * Token fields are OPTIONAL: an adapter that does not report usage (CLI
 * subscription, or a `usage` object missing the counts) leaves them `undefined`
 * so the voter stays honestly UNMEASURED downstream — never a fabricated 0.
 */
export interface VoteUsage {
  readonly inputTokens?: number | undefined;
  readonly outputTokens?: number | undefined;
  /** Input tokens read from an existing prompt cache, when reported (#4435). */
  readonly cachedInputTokens?: number | undefined;
  /** Input tokens spent writing the cache, when reported (#4435). */
  readonly cacheCreationInputTokens?: number | undefined;
}

/** Read a usage token count when the adapter actually reported a number (#3910). */
function readTokenCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** One completion attempt: build → complete (timeout-bounded) → extract text + usage. */
interface VoteCompletionArgs {
  readonly role: VoterRole;
  readonly proposal: string;
  readonly adapter: IModelAdapter;
  readonly timeoutMs: number;
  readonly withResponseFormat: boolean;
  /** Declared options for a multi-option proposal (#4472). */
  readonly options?: readonly string[] | undefined;
  /**
   * The target project named in the system prompt (#6110). Required as a KEY
   * so a hop that forgets to pass it fails to compile; `undefined` is the
   * `nexus-agents` default.
   */
  readonly project: string | undefined;
  /**
   * The working directory every seat's tools run in (#6254), named in the
   * user prompt. Required as a KEY for the same reason as `project`;
   * `undefined` renders no REPOSITORY ACCESS block.
   */
  readonly workspace: string | undefined;
  readonly workspaceSha?: string | undefined;
}

async function runVoteCompletion(args: VoteCompletionArgs): Promise<
  | {
      ok: true;
      output: string;
      usage: VoteUsage;
      cliStderr: string | undefined;
      fallbackFrom: string | undefined;
    }
  | { ok: false; error: string }
> {
  const { role, adapter, timeoutMs } = args;
  const request = buildVoteRequest(args);
  const timeoutResult = await withTimeout(
    adapter.complete(request),
    timeoutMs,
    `Vote timeout after ${String(timeoutMs)}ms for role: ${role}`
  );
  if (!timeoutResult.ok) return { ok: false, error: timeoutResult.error };
  const response = timeoutResult.value;
  if (!response.ok) return { ok: false, error: response.error.message };
  // #3910: capture the adapter-reported per-call usage so it can ride up into
  // the AgentVoteResult and feed the decision-cost rollup as MEASURED. Cast
  // through a loose shape: the type guarantees `usage`, but a real adapter (or a
  // partial response) may omit the counts — read each defensively so a
  // non-reporting call stays unmeasured rather than throwing or fabricating 0.
  const reported = response.value.usage as unknown as
    | {
        inputTokens?: unknown;
        outputTokens?: unknown;
        cachedInputTokens?: unknown;
        cacheCreationInputTokens?: unknown;
      }
    | undefined;
  const usage: VoteUsage = {
    inputTokens: readTokenCount(reported?.inputTokens),
    outputTokens: readTokenCount(reported?.outputTokens),
    // #4435: an `inputTokens: 2` next to 3,980 cached tokens tells a very
    // different story than `inputTokens: 2` alone.
    cachedInputTokens: readTokenCount(reported?.cachedInputTokens),
    cacheCreationInputTokens: readTokenCount(reported?.cacheCreationInputTokens),
  };
  return {
    ok: true,
    output: extractTextFromResponse(response.value.content),
    usage,
    // #6094: the transport's captured stderr rides up with the vote so the
    // caller can classify a seat that could not read the artifact from the
    // structured signal rather than from its prose.
    cliStderr: response.value.cliStderr,
    // #6115: the alias the seat asked for, when the CLI answered on another
    // one of its family (#6120) — the seat's result discloses it as a fallback.
    fallbackFrom: response.value.fallbackFrom,
  };
}

/** A parsed vote plus what the transport reported alongside it. Module-private: its only consumer is the return type below. */
interface VoteAttemptSuccess {
  readonly ok: true;
  readonly vote: Vote;
  readonly output: string;
  readonly usage: VoteUsage;
  /** Stderr the CLI transport captured for this completion, when any (#6094). */
  readonly cliStderr: string | undefined;
  /** The requested model alias when the CLI answered on another of its family (#6120). */
  readonly fallbackFrom: string | undefined;
}

/** What the prompt carries beyond the proposal: declared options (#4472), the target project (#6110) and the working directory (#6254). */
interface VotePromptContext {
  readonly options?: readonly string[] | undefined;
  /** Target project for the system prompt; omitted ⇒ `nexus-agents`. */
  readonly project?: string | undefined;
  /** Working directory named in the user prompt; omitted ⇒ no REPOSITORY ACCESS block. */
  readonly workspace?: string | undefined;
  readonly workspaceSha?: string | undefined;
}

/**
 * A failed attempt. `cliStderr` is present only on the parse-failure branch —
 * the transport completed but the output was not a vote (#6269): it says WHY
 * the answer was empty. An adapter error carries its cause in `error`.
 */
interface VoteAttemptFailure {
  readonly ok: false;
  readonly error: string;
  readonly cliStderr?: string | undefined;
}

export async function executeSingleVoteAttempt(
  role: VoterRole,
  proposal: string,
  adapter: IModelAdapter,
  timeoutMs: number,
  context: VotePromptContext = {}
): Promise<VoteAttemptSuccess | VoteAttemptFailure> {
  const completionArgs = {
    role,
    proposal,
    adapter,
    timeoutMs,
    project: context.project,
    workspace: context.workspace,
    ...context,
  };
  let completion = await runVoteCompletion({ ...completionArgs, withResponseFormat: true });
  // #3497: retry once WITHOUT responseFormat when the backend rejects the
  // tool-use-backed structured-output ask, so the panel keeps full strength.
  if (!completion.ok && isStructuredOutputUnsupported(completion.error)) {
    completion = await runVoteCompletion({ ...completionArgs, withResponseFormat: false });
  }
  if (!completion.ok) return { ok: false, error: completion.error };

  try {
    // parseVoteResponse throws SyntheticVoteError if parsing fails — we only
    // accept real LLM votes, not synthetic fallbacks.
    const vote = parseVoteResponse(completion.output, role, context.options);
    return {
      ok: true,
      vote,
      output: completion.output,
      usage: completion.usage,
      cliStderr: completion.cliStderr,
      fallbackFrom: completion.fallbackFrom,
    };
  } catch (error) {
    if (error instanceof SyntheticVoteError) {
      return {
        ok: false,
        error: `Vote parsing failed: ${error.message}`,
        cliStderr: completion.cliStderr,
      };
    }
    throw error; // Re-throw unexpected errors
  }
}

/** Options for executeWithRetries. */
export interface RetryOptions {
  readonly role: VoterRole;
  readonly proposal: string;
  readonly adapter: IModelAdapter;
  readonly logger: ILogger;
  readonly timeoutMs: number;
  readonly maxRetries: number;
  /** Declared options for a multi-option proposal (#4472); absent for yes/no. */
  readonly options?: readonly string[] | undefined;
  /** Target project for the system prompt (#6110); absent ⇒ `nexus-agents`. */
  readonly project?: string | undefined;
  /** Working directory named in the user prompt (#6254); absent ⇒ no REPOSITORY ACCESS block. */
  readonly workspace?: string | undefined;
  readonly workspaceSha?: string | undefined;
}

/**
 * Executes vote attempts with retry logic.
 * Returns the error message from last failed attempt, or undefined if successful.
 */
/**
 * Record that a voter gave up its remaining attempts (#5359).
 *
 * A DURABLE capacity cap — a spend or usage ceiling on the credential — does
 * not clear in seconds, so the remaining attempts are guaranteed-futile. The
 * waste is not local either: `computeOverallConsensusDeadlineMs` budgets
 * `timeoutMs * (maxRetries + 1)` as a SHARED wall-clock deadline across the
 * panel, so burning it here starved a healthy voter on a different adapter in
 * four consecutive live runs. Failing fast hands the remaining budget to the
 * #3587 fallback, which is what actually recovers the voice.
 */
function logAbandonedRetries(
  logger: ILogger,
  role: VoterRole,
  attempt: number,
  maxRetries: number,
  cause: 'durable capacity cap' | 'auth failure'
): void {
  logger.warn(`${cause} — abandoning retries for this voter`, {
    role,
    attempt: attempt + 1,
    remainingAttemptsSkipped: maxRetries - attempt,
  });
}

/**
 * What a successful vote attempt hands the result builder: the parsed vote,
 * its usage, the transport's stderr (#6094) and any in-family model
 * substitution (#6120, disclosed as a fallback by #6115).
 */
export interface VoteOutcome {
  readonly vote: Vote;
  readonly usage: VoteUsage;
  readonly cliStderr: string | undefined;
  readonly fallbackFrom: string | undefined;
}

export async function executeWithRetries(
  opts: RetryOptions
): Promise<(VoteOutcome & { ok: true }) | { error: string; ok: false }> {
  const { role, proposal, adapter, logger, timeoutMs, maxRetries } = opts;
  let lastError = '';

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const isRateLimit = isRateLimitError(lastError);
      const baseDelay = isRateLimit ? RATE_LIMIT_RETRY_DELAY_MS : INITIAL_RETRY_DELAY_MS;
      const delayMs = baseDelay * Math.pow(2, attempt - 1);
      logger.debug('Retrying vote execution', { role, attempt, delayMs, isRateLimit });
      await delay(delayMs);
    }

    // #2472: per-attempt timing breakdown so investigators can see which
    // retry succeeded (or which attempt blew the cap). Total vote time
    // is already captured at the call-site; this fills the per-attempt gap.
    const attemptStart = Date.now();
    const result = await executeSingleVoteAttempt(role, proposal, adapter, timeoutMs, opts);
    const attemptMs = Date.now() - attemptStart;
    if (result.ok) {
      logger.info('Vote attempt timing', {
        role,
        attempt: attempt + 1,
        attemptMs,
        succeeded: true,
      });
      const { vote, usage, cliStderr, fallbackFrom } = result;
      return { vote, usage, cliStderr, fallbackFrom, ok: true };
    }

    lastError = result.error;
    const terminal = logFailedAttempt(logger, {
      role,
      attempt,
      maxRetries,
      attemptMs,
      error: lastError,
      cliStderr: result.cliStderr,
    });
    if (terminal !== null) {
      logAbandonedRetries(logger, role, attempt, maxRetries, terminal);
      break;
    }
  }

  return { error: lastError !== '' ? lastError : 'Unknown error after all retries', ok: false };
}

/** One failed attempt, as {@link logFailedAttempt} reports it. */
interface FailedAttempt {
  readonly role: VoterRole;
  readonly attempt: number;
  readonly maxRetries: number;
  readonly attemptMs: number;
  readonly error: string;
  /** Stderr the transport captured when the output was not a vote (#6269). */
  readonly cliStderr: string | undefined;
}

/** Longest stderr excerpt carried on the `Vote attempt failed` line (#6269). */
const LOGGED_STDERR_MAX_CHARS = 200;

/**
 * The first non-blank stderr line, secret-redacted and clipped: enough to name
 * the cause (an auth line, a sandbox failure) without a stack trace (#6269).
 */
function loggedStderrLine(cliStderr: string): string {
  const first = cliStderr.split('\n').find((line) => line.trim() !== '') ?? '';
  return sanitizeOutput(first.trim()).slice(0, LOGGED_STDERR_MAX_CHARS);
}

/**
 * Log one failed attempt's timing and classification. Returns the reason the
 * remaining attempts are futile — a DURABLE capacity cap (#5359) or an auth
 * failure (#6269: a retry on the same credential cannot clear it, and the
 * budget it would burn is what the #3587 fallback needs) — or `null` when the
 * caller should keep retrying.
 */
function logFailedAttempt(
  logger: ILogger,
  failed: FailedAttempt
): 'durable capacity cap' | 'auth failure' | null {
  const { role, attempt, maxRetries, attemptMs, error, cliStderr } = failed;
  const rateLimited = isRateLimitError(error);
  const durableCap = isDurableCapacityText(error);
  const authFailure = isAuthFailureText(error);
  logger.info('Vote attempt timing', {
    role,
    attempt: attempt + 1,
    attemptMs,
    succeeded: false,
    rateLimited,
  });
  logger.warn('Vote attempt failed', {
    role,
    attempt: attempt + 1,
    maxRetries: maxRetries + 1,
    error,
    ...(rateLimited ? { rateLimited: true } : {}),
    ...(durableCap ? { durableCap: true } : {}),
    ...(authFailure ? { authFailure: true } : {}),
    ...(cliStderr !== undefined && cliStderr !== ''
      ? { cliStderr: loggedStderrLine(cliStderr) }
      : {}),
  });
  if (durableCap) return 'durable capacity cap';
  if (authFailure) return 'auth failure';
  return null;
}
